import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Stack,
} from '@mui/material';
import { Support as SupportIcon, Send as SendIcon } from '@mui/icons-material';
import { apiService } from '../../services/apiService';

type TicketCategory = 'technical' | 'billing' | 'feature_request' | 'bug_report';
type TicketPriority = 'low' | 'medium' | 'high' | 'urgent';

const CATEGORY_OPTIONS: { value: TicketCategory; label: string }[] = [
  { value: 'technical', label: 'Technical issue' },
  { value: 'billing', label: 'Billing & subscription' },
  { value: 'feature_request', label: 'Feature request' },
  { value: 'bug_report', label: 'Bug report' },
];

const PRIORITY_OPTIONS: { value: TicketPriority; label: string }[] = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

interface SupportTicketFormProps {
  onSubmitted?: () => void;
}

const SupportTicketForm: React.FC<SupportTicketFormProps> = ({ onSubmitted }) => {
  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState<TicketCategory>('technical');
  const [priority, setPriority] = useState<TicketPriority>('medium');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const resetForm = () => {
    setSubject('');
    setCategory('technical');
    setPriority('medium');
    setDescription('');
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setSuccess(null);

    if (!subject.trim() || !description.trim()) {
      setError('Please provide both a subject and a description.');
      return;
    }

    try {
      setSubmitting(true);
      const response = await apiService.createSupportTicket({
        subject: subject.trim(),
        description: description.trim(),
        category,
        priority,
      });

      if (response.success && response.data) {
        const ref = response.data.ticket_number || response.data.id;
        setSuccess(
          ref
            ? `Your support request has been submitted (reference ${ref}). We'll be in touch soon.`
            : "Your support request has been submitted. We'll be in touch soon."
        );
        resetForm();
        onSubmitted?.();
      } else {
        setError(response.message || 'Unable to submit your request. Please try again.');
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Unable to submit your request. Please try again.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <SupportIcon color="primary" />
          <Typography variant="h5" component="h2">
            Support &amp; Help
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" paragraph>
          Email{' '}
          <a href="mailto:support@branchrouter.com">support@branchrouter.com</a>
          {' '}anytime, or send a ticket below with as much detail as you can.
          {' '}
          <a href="https://branchrouter.com/privacy.html" target="_blank" rel="noreferrer">
            Privacy Policy
          </a>
          {' · '}
          <a href="https://branchrouter.com/terms.html" target="_blank" rel="noreferrer">
            Terms of Service
          </a>
        </Typography>

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Stack spacing={2}>
            <TextField
              label="Subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              required
              fullWidth
              inputProps={{ maxLength: 200 }}
              disabled={submitting}
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <FormControl fullWidth disabled={submitting}>
                <InputLabel id="support-category-label">Category</InputLabel>
                <Select
                  labelId="support-category-label"
                  label="Category"
                  value={category}
                  onChange={(e) => setCategory(e.target.value as TicketCategory)}
                >
                  {CATEGORY_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth disabled={submitting}>
                <InputLabel id="support-priority-label">Priority</InputLabel>
                <Select
                  labelId="support-priority-label"
                  label="Priority"
                  value={priority}
                  onChange={(e) => setPriority(e.target.value as TicketPriority)}
                >
                  {PRIORITY_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            <TextField
              label="How can we help?"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
              fullWidth
              multiline
              minRows={5}
              placeholder="Describe your question or the issue you're seeing, including any steps to reproduce it."
              disabled={submitting}
            />

            <Box>
              <Button
                type="submit"
                variant="contained"
                startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
                disabled={submitting}
              >
                {submitting ? 'Submitting…' : 'Submit Request'}
              </Button>
            </Box>
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
};

export default SupportTicketForm;
